<template>
  <Layout>
    <div class="lg:flex lg:-mx-6">
      <div class="lg:w-2/3 lg:mx-6 mb-10">
        <div class="inline-block">
          <CompanyBackLink />
        </div>
        <div class="flex items-center justify-between">
          <h1 class="h1">{{ contact.name }}</h1>
          <BaseDropdown icon="more-vertical" iconSize="22" buttonClass="btn btn-gray p-1">
            <router-link
              :to="{ name: 'contacts.edit', params: { id } }"
              class="dropdown-item w-32"
            >Wijzigen</router-link>
            <DeleteConfirmButton
              class="dropdown-item w-32"
              :message="`Weet je zeker dat je dit contact wilt verwijderen?`"
              @confirm="deleteContact()"
            >Verwijderen</DeleteConfirmButton>
          </BaseDropdown>
        </div>

        <div class="mt-2">
          <TagList :tags="contact.contact.tags" />
        </div>

        <Tabs class="lg:hidden mt-10">
          <Tab name="Details" class="my-4">
            <CompanyDetails class="lg:mx-4" :contact="contact" />
          </Tab>
          <Tab
            v-if="isOrganization"
            name="Contactpersonen"
            class="my-4"
            :indicator="contact.contact.people.length"
          >
            <CompanyPersonList :people="contact.contact.people" />
          </Tab>
        </Tabs>

        <Tabs class="mt-10">
          <Tab name="Pre-Deals" class="my-4">
            <CompanyDeal
              v-for="deal in predeals"
              :deal="deal"
              :contact="contact"
              :key="`deal-${deal.id}`"
            />
            <div
              v-if="!predeals.length"
              class="text-gray-400"
            >Nog geen Pre-Deals bij {{ contact.name }}</div>
          </Tab>
          <Tab name="Deals" class="my-4" :indicator="deals.length">
            <CompanyDeal
              v-for="deal in deals"
              :deal="deal"
              :contact="contact"
              :key="`deal-${deal.id}`"
            />
            <div v-if="!deals.length" class="text-gray-400">Nog geen Deals bij {{ contact.name }}</div>
          </Tab>
        </Tabs>
        <ModalButton class="btn btn-secondary" label="Nieuwe deal toevoegen">
          <DealForm
            :action="`/json/contacts/${id}/deals`"
            :contact="contact"
            @success="setCompany"
          />
        </ModalButton>

        <div class="tabs mb-4 mt-12">
          <div class="tab active cursor-default">Notities &amp; Reminders</div>
          <div class="tab-remainder"></div>
        </div>
        <NoteReminderWithDeal
          v-for="note in contact.reminders"
          :contact="contact"
          :note="note"
          :key="`reminder-${note.id}`"
          class="mb-2"
        />
        <div
          v-if="!contact.reminders.length"
          class="text-gray-400"
        >Geen notities met reminder bij {{ contact.name }}</div>
        <div class="my-4 md:flex justify-between items-center">
          <ModalButton class="btn btn-secondary" label="Nieuwe notitie toevoegen">
            <NoteForm
              :action="`/json/contacts/${$store.state.contact.id}/notes`"
              @success="setCompany"
            />
          </ModalButton>
          <BaseCheckbox
            v-model="showAllActivities"
            class="py-2 border-t mt-4 w-full md:border-0 md:mt-0 md:w-auto border-gray-300"
          >Toon automatische meldingen ({{hiddenActivitiesCount}})</BaseCheckbox>
        </div>
        <ActivityList :activities="contact.activities" :showAll="showAllActivities" />
      </div>
      <div class="hidden lg:block lg:w-1/3 lg:mx-6 lg:pl-8">
        <CompanyStatusManager class="mt-5 flex" />

        <h3 class="mx-4 mb-2 mt-6">Details</h3>
        <CompanyDetails class="lg:mx-4" :contact="contact" />

        <div v-if="isOrganization">
          <h3 class="mx-4 mb-2 mt-6">Contactpersonen</h3>
          <CompanyPersonList :people="contact.contact.people" />
          <router-link
            :to="{ name: 'contacts.people.create', params: { id } }"
            class="btn btn-secondary block text-center"
          >Contactpersoon toevoegen</router-link>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import { destroy } from "@/api.js";
import store from "@/store.js";
import CompanyStatusManager from "@/components/CompanyStatusManager";
import CompanyDeal from "@/components/CompanyDeal";
import NoteReminderWithDeal from "@/components/NoteReminderWithDeal";
import ActivityList from "@/components/ActivityList";
import CompanyDetails from "@/components/CompanyDetails";
import CompanyPersonList from "@/components/CompanyPersonList";
import DealForm from "@/components/DealForm";
import NoteForm from "@/components/NoteForm";
import TagList from "@/components/TagList";
import CompanyBackLink from "@/components/CompanyBackLink";
import DeleteConfirmButton from "@/components/DeleteConfirmButton";
import Tabs from "@/components/Tabs";
import Tab from "@/components/Tab";

export default {
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },

  components: {
    CompanyStatusManager,
    CompanyDeal,
    NoteReminderWithDeal,
    ActivityList,
    CompanyDetails,
    CompanyPersonList,
    DealForm,
    NoteForm,
    TagList,
    CompanyBackLink,
    DeleteConfirmButton,
    Tabs,
    Tab
  },

  metaInfo() {
    return { title: this.contact.name };
  },

  data() {
    return {
      tab: "predeals",
      showAllActivities: false
    };
  },

  computed: {
    contact() {
      return this.$store.state.contact;
    },

    isOrganization() {
      return this.contact.contact_type == "organization";
    },

    deals() {
      return this.contact.deals
        .filter(deal => deal.result != "pending")
        .sort((a, b) => (a.closed_at < b.closed_at ? 1 : -1));
    },

    predeals() {
      return this.contact.deals.filter(deal => deal.result == "pending");
    },

    hiddenActivitiesCount() {
      return this.contact.activities.filter(a => !a.is_visible).length;
    }
  },

  beforeRouteEnter(to, from, next) {
    store
      .dispatch("loadContact", to.params.id)
      .then(() => next())
      .catch(error => {
        if (error.response.status == 404) {
          store.commit("FLASH_ADD", {
            message: "Contact niet gevonden (mogelijk verwijderd)",
            type: "warning"
          });
        }
      });
  },

  beforeRouteUpdate(to, from, next) {
    store
      .dispatch("loadContact", to.params.id)
      .then(() => next())
      .catch(error => {
        if (error.response.status == 404) {
          store.commit("FLASH_ADD", {
            message: "Contact niet gevonden (mogelijk verwijderd)",
            type: "warning"
          });
        }
      });
  },

  methods: {
    setCompany(data) {
      this.$store.commit("SET_CONTACT", data);
    },

    deleteContact() {
      destroy(`/contacts/${this.id}`).then(() => {
        this.$store.commit("FLASH_ADD", { message: "Contact is verwijderd!" });
        this.$router.push({ name: "contacts" });
      });
    }
  }
};
</script>
